'use client';

import React, { useState } from 'react';
import { ChevronLeft, CheckCircle, CreditCard, Smartphone, Building, Loader2, MapPin, Calendar, Clock, User, Phone, Mail, FlaskConical, Package } from 'lucide-react';
import type { BookingData } from './BookingWizard';
import { Toaster } from 'sonner';
import { useRouter } from 'next/navigation';

const paymentMethods = [
  { id: 'online', label: 'Online Payment', sub: 'UPI, Cards, Net Banking', icon: CreditCard },
  { id: 'upi', label: 'UPI / QR Code', sub: 'PhonePe, GPay, Paytm', icon: Smartphone },
  { id: 'counter', label: 'Pay at Counter', sub: 'Cash or Card at branch', icon: Building },
];

export default function Step4Summary({
  bookingData,
  onBack,
}: {
  bookingData: BookingData;
  onBack: () => void;
}) {
  const [selectedPayment, setSelectedPayment] = useState('online');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const router = useRouter();

  const subtotal = bookingData.service?.price ?? 0;
  const homeCharge = bookingData.homeCollection ? 99 : 0;
  const total = subtotal + homeCharge;

  const handleConfirm = async () => {
    setLoading(true);
    // Backend integration point: POST /api/appointments with bookingData + paymentMethod
    await new Promise((r) => setTimeout(r, 1800));
    setLoading(false);
    const appointmentId = `LAB-${Math.floor(Math.random() * 90000) + 10000}`;
    const params = new URLSearchParams({
      id: appointmentId,
      service: bookingData.service?.name ?? '',
      date: bookingData.date,
      time: bookingData.timeSlot,
      location: bookingData.homeCollection ? bookingData.homeAddress || 'Home Collection' : bookingData.branchName,
      patient: bookingData.patientName,
      amount: `₹${total.toLocaleString('en-IN')}`,
      home: bookingData.homeCollection ? 'true' : 'false',
    });
    // Include patient email so booking-success page can send confirmation email
    if (bookingData.patientEmail) {
      params.set('email', bookingData.patientEmail);
    }
    router.push(`/booking-success?${params.toString()}`);
  };

  if (confirmed) {
    return (
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <Toaster position="bottom-right" richColors />
        <div className="p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-green-50 border-4 border-green-200 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-extrabold text-foreground mb-2">Appointment Confirmed!</h2>
          <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
            Your appointment has been successfully booked. A confirmation has been sent to your WhatsApp and email.
          </p>

          <div className="bg-muted/50 rounded-xl p-5 max-w-sm mx-auto mb-8 text-left space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-muted-foreground w-28">Booking ID:</span>
              <span className="font-bold text-primary font-mono">LAB-{Math.floor(Math.random() * 90000) + 10000}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-muted-foreground w-28">Service:</span>
              <span className="text-foreground">{bookingData.service?.name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-muted-foreground w-28">Date & Time:</span>
              <span className="text-foreground">{bookingData.date} · {bookingData.timeSlot}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-muted-foreground w-28">Total Paid:</span>
              <span className="font-bold text-green-600">₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <a href="https://wa.me/911234567890" target="_blank" rel="noopener noreferrer" className="btn-green text-sm py-2.5">
              View on WhatsApp
            </a>
            <a href="/" className="btn-outline text-sm py-2.5">
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <Toaster position="bottom-right" richColors />
      <div className="px-6 py-5 border-b border-border">
        <h2 className="font-bold text-xl text-foreground">Review & Confirm</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Review your booking details before confirming.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Summary grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Service */}
          <div className="bg-muted/40 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              {bookingData.service?.type === 'package' ? (
                <Package size={15} className="text-primary" />
              ) : (
                <FlaskConical size={15} className="text-primary" />
              )}
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Service</span>
            </div>
            <p className="font-bold text-sm text-foreground">{bookingData.service?.name}</p>
            {bookingData.service?.tests && (
              <p className="text-xs text-muted-foreground mt-0.5">{bookingData.service.tests} tests included</p>
            )}
          </div>

          {/* Location */}
          <div className="bg-muted/40 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={15} className="text-primary" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Location</span>
            </div>
            {bookingData.homeCollection ? (
              <>
                <p className="font-bold text-sm text-foreground">Home Collection</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{bookingData.homeAddress || 'Address provided'}</p>
              </>
            ) : (
              <p className="font-bold text-sm text-foreground">{bookingData.branchName}</p>
            )}
          </div>

          {/* Date & Time */}
          <div className="bg-muted/40 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={15} className="text-primary" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Appointment</span>
            </div>
            <p className="font-bold text-sm text-foreground">{bookingData.date}</p>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              <Clock size={10} />
              {bookingData.timeSlot}
            </p>
          </div>

          {/* Patient */}
          <div className="bg-muted/40 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <User size={15} className="text-primary" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Patient</span>
            </div>
            <p className="font-bold text-sm text-foreground">{bookingData.patientName}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Age {bookingData.patientAge} · {bookingData.patientGender}
            </p>
            {bookingData.patientPhone && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Phone size={10} />
                +91 {bookingData.patientPhone}
              </p>
            )}
            {bookingData.patientEmail && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Mail size={10} />
                {bookingData.patientEmail}
              </p>
            )}
          </div>
        </div>

        {/* Price breakdown */}
        <div className="bg-muted/40 rounded-xl p-5">
          <h3 className="font-semibold text-sm text-foreground mb-4">Price Breakdown</h3>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{bookingData.service?.name}</span>
              <span className="font-medium text-foreground">₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            {bookingData.service?.originalPrice && bookingData.service.originalPrice > subtotal && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Package Discount</span>
                <span className="text-green-600 font-medium">
                  −₹{(bookingData.service.originalPrice - subtotal).toLocaleString('en-IN')}
                </span>
              </div>
            )}
            {bookingData.homeCollection && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Home Collection Charge</span>
                <span className="font-medium text-foreground">₹{homeCharge}</span>
              </div>
            )}
            <div className="flex items-center justify-between font-bold text-base pt-3 border-t border-border">
              <span className="text-foreground">Total Amount</span>
              <span className="text-primary stats-number text-xl">₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Payment method */}
        <div>
          <h3 className="font-semibold text-sm text-foreground mb-4 flex items-center gap-2">
            <CreditCard size={15} className="text-primary" />
            Payment Method
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {paymentMethods.map((pm) => (
              <button
                key={`pm-${pm.id}`}
                onClick={() => setSelectedPayment(pm.id)}
                className={`text-left p-4 rounded-xl border-2 transition-all duration-150 ${
                  selectedPayment === pm.id
                    ? 'border-primary bg-secondary' :'border-border bg-card hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-2.5 mb-1.5">
                  <pm.icon size={16} className={selectedPayment === pm.id ? 'text-primary' : 'text-muted-foreground'} />
                  <span className="font-semibold text-sm text-foreground">{pm.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{pm.sub}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Terms */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 leading-relaxed">
          By confirming, you agree to our cancellation policy: free cancellation up to 2 hours before appointment.
          Reports will be delivered digitally within 24–48 hours of sample collection.
        </div>

        {/* Navigation */}
        <div className="pt-4 border-t border-border flex items-center justify-between">
          <button onClick={onBack} className="btn-outline py-2.5 px-5 text-sm flex items-center gap-2">
            <ChevronLeft size={15} />
            Back
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="btn-primary min-w-[180px] justify-center"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <CheckCircle size={15} />
                Confirm & Pay ₹{total.toLocaleString('en-IN')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}